// F-110 : réglages IA de l'espace, information fournisseur, volumes et coûts estimés des appels.
import { useEffect, useState } from "react";
import { put } from "@core/api";
import { dateTime } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, KeyValues, Loading, Notice, PageHeader, Table, useToast } from "@core/ui";
import { Mode, microEuros, useAction } from "./shared";

const OPERATION: Record<string, string> = { extraction: "Lecture de pièce", traduction: "Traduction", assistant: "Assistant" };

export function AiPage() {
  const org = useOrg();
  const q = useGet<any>(org.api("/ai/overview"));
  const invalidate = useInvalidate();
  const toast = useToast();
  const action = useAction();
  const [enabled, setEnabled] = useState(true);
  const [purposes, setPurposes] = useState<string[]>([]);
  useEffect(() => {
    if (q.data) {
      setEnabled(q.data.settings.enabled);
      setPurposes(q.data.settings.purposes);
    }
  }, [q.data]);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const owner = org.role === "proprietaire";
  const save = async () => {
    const r = await action.run(() => put(org.api("/ai/settings"), { enabled, purposes }));
    if (r) {
      toast("Réglages enregistrés.");
      invalidate(org.api("/ai"));
    }
  };
  const totalCalls = d.months.reduce((s: number, m: any) => s + m.calls, 0);
  const totalEstimate = d.months.reduce((s: number, m: any) => s + m.estimated_cost_microcents, 0);
  return (
    <div className="page">
      <PageHeader eyebrow="Espace" title="IA et services" intro="Ce que l’IA fait dans cet espace, avec quel fournisseur, pour quelles finalités et à quel coût estimé. L’IA propose ; les contrôles et décisions restent les vôtres." />
      <div className="grid-2">
        <Card title="Réglages de l’espace">
          <div className="stack-sm">
            <ErrorBox error={action.error} />
            <Checkbox label="Autoriser l’usage de l’IA dans cet espace" checked={enabled} onChange={owner ? setEnabled : () => undefined} help="Désactivé : aucune pièce, aucun texte n’est envoyé à un service d’IA ; la saisie manuelle reste possible." />
            <fieldset className="stack-sm" disabled={!enabled || !owner}>
              <legend className="small">Finalités autorisées</legend>
              {d.purposes.map((p: any) => (
                <Checkbox
                  key={p.code}
                  label={p.label}
                  checked={purposes.includes(p.code)}
                  onChange={(v) => setPurposes(v ? [...purposes, p.code] : purposes.filter((x) => x !== p.code))}
                />
              ))}
            </fieldset>
            {owner ? (
              <Button busy={action.busy} onClick={save}>
                Enregistrer
              </Button>
            ) : (
              <p className="small muted">Seul un propriétaire de l’espace peut modifier ces réglages.</p>
            )}
            <p className="small muted">Ce réglage s’applique à la traduction et à l’assistant ; la lecture des pièces le respectera dès qu’elle y sera reliée.</p>
          </div>
        </Card>
        <Card title="Fournisseur">
          <KeyValues
            items={[
              ["Fournisseur", d.provider_info.fournisseur],
              ["Clé configurée", d.provider_info.cle_configuree ? "Oui" : "Non : simulateurs utilisés"],
              ["Modèle", d.provider_info.modele ?? "—"],
              ["Conservation", d.provider_info.conservation],
              ["Données envoyées", d.provider_info.donnees],
              ["Sécurité", d.provider_info.securite],
            ]}
          />
          <Notice tone="attention">{d.provider_info.a_confirmer}</Notice>
        </Card>
      </div>
      <Card title="Services">
        <Table caption="Services d’IA">
          <thead>
            <tr>
              <th>Usage</th>
              <th>Service</th>
              <th>Modèle</th>
              <th>Mode</th>
            </tr>
          </thead>
          <tbody>
            {d.providers.map((p: any) => (
              <tr key={p.usage}>
                <td>{p.usage}</td>
                <td className="mono small">{p.name}</td>
                <td>{p.model ?? "—"}</td>
                <td>
                  <Mode mode={p.mode} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      <Card title="Appels des 12 derniers mois" actions={<Badge tone="attention">Coûts estimés : hypothèses</Badge>}>
        {d.months.length === 0 ? (
          <Empty title="Aucun appel enregistré." />
        ) : (
          <>
            <p className="small">
              {totalCalls} appel(s), coût estimé {microEuros(totalEstimate)} si les services étaient réels (coûts unitaires de la page « Coûts »).
            </p>
            <Table caption="Appels par mois">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th>Opération</th>
                  <th>Mode</th>
                  <th className="right">Appels</th>
                  <th className="right">Erreurs</th>
                  <th className="right">Coût estimé</th>
                </tr>
              </thead>
              <tbody>
                {d.months.map((m: any, i: number) => (
                  <tr key={i}>
                    <td className="mono">{m.month}</td>
                    <td>
                      {OPERATION[m.operation] ?? m.operation}
                      <span className="block small muted mono">{m.adapter}</span>
                    </td>
                    <td>
                      <Mode mode={m.mode === "simulateur" ? "simulator" : m.mode} />
                    </td>
                    <td className="right num">{m.calls}</td>
                    <td className="right num">{m.errors}</td>
                    <td className="right num">{microEuros(m.estimated_cost_microcents)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Card>
      <Card title="Derniers appels">
        {d.recent.length === 0 ? (
          <Empty title="Aucun appel." />
        ) : (
          <Table caption="Derniers appels">
            <thead>
              <tr>
                <th>Date</th>
                <th>Opération</th>
                <th>Finalité</th>
                <th>Volume transmis</th>
                <th>Résultat</th>
              </tr>
            </thead>
            <tbody>
              {d.recent.map((c: any) => (
                <tr key={c.id}>
                  <td>{dateTime(c.created_at)}</td>
                  <td>{OPERATION[c.operation] ?? c.operation}</td>
                  <td className="small">{c.purpose || "—"}</td>
                  <td className="small mono">
                    {Object.entries(c.request_meta ?? {})
                      .map(([k, v]) => `${k} : ${v}`)
                      .join(" · ")}
                  </td>
                  <td>{c.status === "ok" ? "Réussi" : <Badge tone="erreur">Erreur</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
